// Create: src/redux/services/permissionService.ts
import { apiSlice } from "../apiSlice"; // Add this import

interface UserPermissions {
  roles: string[];
  permissions: {
    [featureId: string]: {
      list: boolean;
      view: boolean;
      edit: boolean;
      delete: boolean;
    };
  };
}

interface NavigationPermission {
  visible: boolean;
  enabled: boolean;
  hasAccess: boolean;
}

// Function to get all user roles from Firebase token
const getCurrentUserRoles = (): string[] => {
  // Get roles from Firebase token stored in localStorage
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    try {
      // Decode the Firebase token to get custom claims
      const tokenParts = accessToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const roles = payload.roles || [];
        // Return all roles from the token
        return roles.length > 0 ? roles : ['unknown'];
      }
    } catch (error) {
      console.error('Error decoding Firebase token:', error);
    }
  }

  return ["unknown"]; // No access for users without valid token
};

// Helper function to get primary role (for backward compatibility)
const getCurrentUserRole = (): string => {
  const roles = getCurrentUserRoles();
  // Return highest priority role
  if (roles.includes('super_admin')) return 'super_admin';
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('planner')) return 'planner';
  if (roles.includes('call_center_agent')) return 'call_center_agent';
  return roles[0] || 'unknown';
};

// Add the interface for permission matrix data
export interface PermissionMatrixRoleItem {
  id: string;
  displayName: string;
  description: string;
  roleName: string;
  isActive: boolean;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  createdBy?: string;
  permissions?: {
    [featureId: string]: {
      list: boolean;
      view: boolean;
      edit: boolean;
      delete: boolean;
    };
  };
}

export interface GetAllFeatureGroupsResponse {
    featureGroups: FeatureGroupItem[];
}

export interface FeatureGroupItem {
    id: string;
    displayName?: string; 
    name?: string; 
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
}

export interface FeatureItem {
    id: string;
    featureGroupId?: string; 
    displayName: string;
    description: string;
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt?: {
        _seconds: number;
        _nanoseconds: number;
    };
    isActive?: boolean; 
}

interface GetAllPermissionMatrixDataResponse extends Array<PermissionMatrixRoleItem> { }
export interface GetAllFeaturesResponse extends Array<FeatureItem> { }

// Helper function to merge permissions from multiple roles (OR logic - most permissive wins)
const mergePermissions = (
  permissions1: { [featureId: string]: { list: boolean; view: boolean; edit: boolean; delete: boolean } },
  permissions2: { [featureId: string]: { list: boolean; view: boolean; edit: boolean; delete: boolean } }
): { [featureId: string]: { list: boolean; view: boolean; edit: boolean; delete: boolean } } => {
  const merged = { ...permissions1 };
  
  Object.keys(permissions2).forEach(featureId => {
    if (!merged[featureId]) {
      merged[featureId] = permissions2[featureId];
    } else {
      // Use OR logic - if either role has permission, grant it
      merged[featureId] = {
        list: merged[featureId].list || permissions2[featureId].list,
        view: merged[featureId].view || permissions2[featureId].view,
        edit: merged[featureId].edit || permissions2[featureId].edit,
        delete: merged[featureId].delete || permissions2[featureId].delete,
      };
    }
  });
  
  return merged;
};

// Define role-based permissions
const getRolePermissions = (role: string, apiData?: PermissionMatrixRoleItem[]): UserPermissions => {
  // If API data is available, use it
  if (apiData && apiData.length > 0) {
    const roleData = apiData.find(roleItem =>
      roleItem.roleName.toLowerCase() === role.toLowerCase() ||
      roleItem.displayName.toLowerCase() === role.toLowerCase()
    ); 

    if (roleData && roleData.permissions) {
      return {
        roles: [role],
        permissions: roleData.permissions
      };
    }
  }

  // 🔥 Fallback to hardcoded permissions if API data not available
  switch (role.toLowerCase()) {
    case "super_admin":
      return {
        roles: ["super_admin"],
        permissions: {
          "feat_home_access": { list: true, view: true, edit: true, delete: true },
          "feat_payments_access": { list: true, view: true, edit: true, delete: true },
          "feat_config_access": { list: true, view: true, edit: true, delete: true },
          "feat_products_access": { list: true, view: true, edit: true, delete: true },
          "feat_menu_access": { list: true, view: true, edit: true, delete: true },
          "feat_orders_access": { list: true, view: true, edit: true, delete: true },
          "feat_cart_access": { list: true, view: true, edit: true, delete: true },
          "feat_distribution_access": { list: true, view: true, edit: true, delete: true },
          "feat_payment_refunds": { list: true, view: true, edit: true, delete: true },
          "feat_payment_returns": { list: true, view: true, edit: true, delete: true },
          "feat_order_issue": { list: true, view: true, edit: true, delete: true },
        }
      };

    case "admin":
    case "role_1":
      return {
        roles: ["admin"],
        permissions: {
          "feat_home_access": { list: true, view: true, edit: true, delete: true },
          "feat_payments_access": { list: true, view: true, edit: true, delete: true },
          "feat_config_access": { list: true, view: true, edit: true, delete: true },
          "feat_products_access": { list: true, view: true, edit: true, delete: true },
          "feat_menu_access": { list: true, view: true, edit: true, delete: true },
          "feat_orders_access": { list: true, view: true, edit: true, delete: true },
          "feat_cart_access": { list: true, view: true, edit: true, delete: true },
          "feat_distribution_access": { list: true, view: true, edit: true, delete: true },
          "feat_payment_refunds": { list: true, view: true, edit: true, delete: true },
          "feat_payment_returns": { list: true, view: true, edit: true, delete: true },
          "feat_order_issue": { list: true, view: true, edit: true, delete: true },
        }
      };

    case "planner":
    case "role_2":
      return {
        roles: ["planner"],
        permissions: {
          "feat_home_access": { list: true, view: true, edit: false, delete: false },
          "feat_orders_access": { list: true, view: true, edit: true, delete: false },
          "feat_cart_access": { list: true, view: true, edit: true, delete: false },
          "feat_distribution_access": { list: true, view: true, edit: true, delete: true },
          "feat_order_issue": { list: false, view: false, edit: false, delete: false },
        }
      };

    case "call_center_agent":
    case "role_3":
      return {
        roles: ["call_center_agent"],
        permissions: {
          "feat_home_access": { list: true, view: true, edit: false, delete: false },
          "feat_orders_access": { list: true, view: true, edit: true, delete: false },
          "feat_config_access": { list: true, view: true, edit: true, delete: true },
          "feat_order_issue": { list: true, view: true, edit: true, delete: false },
        }
      };

    default:
      // No access for unknown roles
      return {
        roles: ["unknown"],
        permissions: {}
      };
  }
};


const getCurrentUserPermissions = (apiData?: PermissionMatrixRoleItem[]): UserPermissions => {
  const currentRoles = getCurrentUserRoles();
  
  // If user has only one role, return its permissions directly
  if (currentRoles.length === 1) {
    return getRolePermissions(currentRoles[0], apiData);
  }
  
  // Merge permissions from all roles
  let mergedPermissions: UserPermissions = {
    roles: currentRoles,
    permissions: {}
  };
  
  currentRoles.forEach(role => {
    const rolePermissions = getRolePermissions(role, apiData);
    mergedPermissions.permissions = mergePermissions(
      mergedPermissions.permissions,
      rolePermissions.permissions
    );
  });
  
  return mergedPermissions;
};

// const navigationFeatureMap: { [key: string]: string | string[] } = {
//   // Main navigation items - using actual feature IDs from your API
//   "home": ["PSkiAf90Q03VzcGC3axe"], // feat_home_access
//   "payments": ["SBdiSnmFdeUq36HfG1ka", "T6uztAmGyPv5yXhsLJ1V"], // feat_customer_refunds, feat_return_requests
//   "configuration": ["2g5D9JUFwpLo9rDQ26JD", "CtxpG79LOuXutopjvEZ3", "FSBVjOkrCpNSNIQqpXgI", "JeCIgFiHBHMy4hncRJqi", "Rn636XXbzV4BZjlzerLh", "MIVHo73t845iP6spTMqS", "c8EqrtMOJZ0dgDuL9gxP"], // All config features
//   "products": ["nEgSO9bmysyuayZjyqiK", "r4kD5iP6Xv9Ya1iXzWtq", "E8sVpMdINUN3Wduj6fqd"], // menu_item, combo_menu, menu-customize
//   "menu": ["uJmQ9yecIYhSuyEL246D"], // menu-list
//   "orders": ["ICH1YjiJ93kJPpuJrB5A", "J36ybuwyjc5zZZ0lKLzj", "JgWhIltaYSAI7SyFhTFm","pdfTs6j0tY1ofimIa8fS"], // order-list, add-order, delivery-order
//   "carts": ["ekkx1Yp1LbTEgeVrKZju"], // cart-list
//   "distribution": ["y8etDsaPRwVzlTQ5PS7r", "8Nx4GVC83XLPe6PS0vp2", "FtIRFWhSLLd4mfTrUsLL", "tA8FJjM3wucDaMTEroFS", "dsE1ZcVfBTJ72zEQdbcc"], // All distribution features

//   // Payment sub-menu items
//   "customer-refunds": ["SBdiSnmFdeUq36HfG1ka"], // feat_customer_refunds
//   "return-requests": ["T6uztAmGyPv5yXhsLJ1V"], // feat_return_requests

//   // Configuration sub-menu items
//   "customize-menu-type": ["2g5D9JUFwpLo9rDQ26JD"], // customize_menu_type
//   "customize-day-available": ["CtxpG79LOuXutopjvEZ3"], // customize_day_available
//   "portion-sizes": ["FSBVjOkrCpNSNIQqpXgI"], // portion_sizes
//   "commission": ["JeCIgFiHBHMy4hncRJqi"], // commission
//   "filter-tags": ["c8EqrtMOJZ0dgDuL9gxP"], // filter_tags
//   "promotions": ["Rn636XXbzV4BZjlzerLh"], // promotions
//   "delivery-zones": ["MIVHo73t845iP6spTMqS"], // delivery_zones

//   // Products sub-menu items
//   "menu-item": ["nEgSO9bmysyuayZjyqiK"], // menu_item
//   "combo-menu": ["r4kD5iP6Xv9Ya1iXzWtq"], // combo_menu
//   "menu-customize": ["E8sVpMdINUN3Wduj6fqd"], // menu-customize

//   // Menu sub-menu items
//   "menu-list": ["uJmQ9yecIYhSuyEL246D"], // menu-list

//   // Orders sub-menu items
//   "add-order": ["J36ybuwyjc5zZZ0lKLzj"], // add-order
//   "cartItem-request": ["pdfTs6j0tY1ofimIa8fS"], // cartItem-request
//   "order-list": ["ICH1YjiJ93kJPpuJrB5A"], // order-list  
//   "delivery-order": ["JgWhIltaYSAI7SyFhTFm"], // delivery-order
  

//   // Carts sub-menu items
//   "cart-list": ["ekkx1Yp1LbTEgeVrKZju"], // cart-list

//   // Distribution sub-menu items
//   "production-plan-list": ["y8etDsaPRwVzlTQ5PS7r"], // production-plan-list
//   "distributionPlan-list": ["8Nx4GVC83XLPe6PS0vp2"], // distributionPlan-list
//   "cartAllocation-list": ["FtIRFWhSLLd4mfTrUsLL"], // cartAllocation-list
//   "cart-configuration": ["tA8FJjM3wucDaMTEroFS"], // cart-configuration
//   "cart-loading": ["dsE1ZcVfBTJ72zEQdbcc"], // cart-loading
// };

const navigationFeatureMap: { [key: string]: string | string[] } = {
  // Main navigation items - using actual feature IDs from your API
  "home": ["feat_024"], // feat_home_access
  "payments": ["feat_001", "feat_002"], // feat_customer_refunds, feat_return_requests
  "configuration": ["feat_003", "feat_004", "feat_005", "feat_006", "feat_007", "feat_008", "feat_009","feat_026","feat_027"], // All config features
  "product-catalogue": ["feat_010", "feat_011", "feat_012"], // menu_item, combo_menu, menu-customize
  "menu": ["feat_013"], // menu-list
  "orders": ["feat_014", "feat_015", "feat_016", "feat_017", "feat_025"], // order-list, add-order, delivery-order, delivery-route-optimizer
  "customers": ["feat_029"], // customer-list
  "organizations": ["feat_030"], // organization-list
  "carts": ["feat_018"], // cart-list
  "distribution": ["feat_019", "feat_020", "feat_021", "feat_022", "feat_023"], // All distribution features
  "allocation": ["feat_028"], // allocation feature

  // Payment sub-menu items
  "customer-refunds": ["feat_001"], // feat_customer_refunds
  "return-requests": ["feat_002"], // feat_return_requests

  // Configuration sub-menu items
  "customize-menu-type": ["feat_003"], // customize_menu_type
  "customize-day-available": ["feat_004"], // customize_day_available
  "portion-sizes": ["feat_005"], // portion_sizes
  "commission": ["feat_006"], // commission
  "filter-tags": ["feat_007"], // filter_tags
  "promotions": ["feat_008"], // promotions
  "delivery-zones": ["feat_009"], // delivery_zones
  "holidays": ["feat_026"], // holidays
  "order-times": ["feat_027"], // order-times

  // product-catalogue sub-menu items
  "menu-item": ["feat_010"], // menu_item
  "combo-menu": ["feat_011"], // combo_menu
  "menu-customize": ["feat_012"], // menu-customize

  // Menu sub-menu items
  "menu-list": ["feat_013"], // menu-list

  // Orders sub-menu items
  "add-order": ["feat_014"], // add-order
  "cartItem-request": ["feat_015"], // cartItem-request
  "pick-up-order-list": ["feat_016"], // pick-up-order-list
  "delivery-order-list": ["feat_017"], // delivery-order
  "delivery-route-optimizer": ["feat_025"], // delivery-route-optimizer


  // Carts sub-menu items
  "cart-list": ["feat_018"], // cart-list

  // Distribution sub-menu items
  "production-plan-list": ["feat_019"], // production-plan-list
  "distributionPlan-list": ["feat_020"], // distributionPlan-list
  "cartAllocation-list": ["feat_021"], // cartAllocation-list
  "cart-configuration": ["feat_022"], // cart-configuration
  "cart-loading": ["feat_023"], // cart-loading
};

// 🔥 Add the missing getNavigationPermissionWithAPI function
export const getNavigationPermissionWithAPI = (
  navigationId: string, 
  apiData?: PermissionMatrixRoleItem[]
): NavigationPermission => {
  const userPermissions = getCurrentUserPermissions(apiData);
  const requiredFeatures = navigationFeatureMap[navigationId];
  
  // If no specific permission required, allow access
  if (!requiredFeatures || requiredFeatures.length === 0) {
    return { visible: true, enabled: true, hasAccess: true };
  }
  
  const featuresArray = Array.isArray(requiredFeatures) ? requiredFeatures : [requiredFeatures];
  
  // Check if user has permission
  const hasAccess = featuresArray.some(featureId => 
    userPermissions.permissions[featureId]?.view || 
    userPermissions.permissions[featureId]?.list
  );
  
  return {
    visible: hasAccess,
    enabled: hasAccess,
    hasAccess
  };
};

// Helper functions for role checking
export const getCurrentRole = (): string => {
  return getCurrentUserRole();
};

export const getCurrentRoles = (): string[] => {
  return getCurrentUserRoles();
};

export const hasPermission = (featureId: string, action: 'list' | 'view' | 'edit' | 'delete'): boolean => {
  const permissions = getCurrentUserPermissions();
  return permissions.permissions[featureId]?.[action] || false;
};

export const isSuperAdmin = (): boolean => {
  const roles = getCurrentUserRoles();
  return roles.some(role => role.toLowerCase() === 'super_admin');
};

export const isAdmin = (): boolean => {
  const roles = getCurrentUserRoles();
  return roles.some(role => role.toLowerCase() === 'admin' || role.toLowerCase() === 'super_admin');
};

export const isPlanner = (): boolean => {
  const roles = getCurrentUserRoles();
  return roles.some(role => role.toLowerCase() === 'planner');
};

export const isCallCenterAgent = (): boolean => {
  const roles = getCurrentUserRoles();
  return roles.some(role => role.toLowerCase() === 'call_center_agent');
};

// API configuration
const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_PROD;

export const permissionMatrixService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllPermissionMatrixData: builder.query<GetAllPermissionMatrixDataResponse, void>({
      query: () => ({
        url: `https://superadminhttps-getallroles${API_BASE_DOMAIN}`,
        method: "GET",
        responseHandler: (response) => response.json(),
      }),
    }),
    getAllFeatures: builder.query<GetAllFeaturesResponse, void>({
      query: () => ({
        url: `https://superadminhttps-getallfeature${API_BASE_DOMAIN}`,
        method: "GET",
        responseHandler: (response) => response.json(),
      }),
    }),
    getFeatureGroups: builder.query<GetAllFeatureGroupsResponse, void>({
      query: () => ({
        url: `https://superadminhttps-getallfeaturegroup${API_BASE_DOMAIN}`,
        method: "GET",
        responseHandler: (response) => response.json(),
      }),
    }),
  }),
});

export const {
  useGetAllPermissionMatrixDataQuery,
  useLazyGetAllPermissionMatrixDataQuery,
  useGetAllFeaturesQuery,
  useLazyGetAllFeaturesQuery,
  useGetFeatureGroupsQuery,
  useLazyGetFeatureGroupsQuery,
} = permissionMatrixService;

// 🔥 Create a comprehensive hook that fetches all permission data
export const usePermissionMatrixData = () => {
  const { data: rolesData, isLoading: isLoadingRoles, error: rolesError } = useGetAllPermissionMatrixDataQuery();
  const { data: featuresData, isLoading: isLoadingFeatures, error: featuresError } = useGetAllFeaturesQuery();
  const { data: featureGroupsData, isLoading: isLoadingGroups, error: groupsError } = useGetFeatureGroupsQuery();

  const isLoading = isLoadingRoles || isLoadingFeatures || isLoadingGroups;
  const error = rolesError || featuresError || groupsError;

  return {
    rolesData,
    featuresData,
    featureGroupsData,
    isLoading,
    error,
    // Transformed data
    roles: rolesData || [],
    features: featuresData || [],
    featureGroups: featureGroupsData?.featureGroups || [],
  };
};

// 🔥 Update useUserPermissions to be more comprehensive
export const useUserPermissions = () => {
  const { rolesData, isLoading, error } = usePermissionMatrixData();
  
  const getUserPermissions = () => {
    return getCurrentUserPermissions(rolesData);
  };

  const getPermissionForFeature = (featureId: string, action: 'list' | 'view' | 'edit' | 'delete'): boolean => {
    const permissions = getUserPermissions();
    return permissions.permissions[featureId]?.[action] || false;
  };

  return {
    permissions: getUserPermissions(),
    isLoading,
    error,
    getPermissionForFeature,
    rolesData
  };
};

// 🔥 Update useNavigationWithPermissions to use comprehensive data
export const useNavigationWithPermissions = (navigation: any[]) => {
  const { rolesData, isLoading } = usePermissionMatrixData();

  const getFilteredNavigation = () => {
    if (isLoading) return navigation; // Return original while loading

    return navigation
      .map(navItem => {
        const permission = getNavigationPermissionWithAPI(navItem.id, rolesData);

        const updatedNavItem = {
          ...navItem,
          isVisible: permission.visible,
          isEnabled: permission.enabled,
          hasAccess: permission.hasAccess,
        };

        // Handle sub-menu items
        if (navItem.subMenu) {
          const visibleSubMenus = navItem.subMenu
            .map((subItem: any) => {
              const subPermission = getNavigationPermissionWithAPI(subItem.id, rolesData);
              return {
                ...subItem,
                isVisible: subPermission.visible,
                isEnabled: subPermission.enabled,
                hasAccess: subPermission.hasAccess,
              };
            })
            .filter((subItem: any) => subItem.isVisible);

          updatedNavItem.subMenu = visibleSubMenus;

          if (visibleSubMenus.length === 0 && !permission.hasAccess) {
            updatedNavItem.isVisible = false;
          }
        }

        return updatedNavItem;
      })
      .filter(navItem => navItem.isVisible);
  };

  return {
    navigation: getFilteredNavigation(),
    isLoading,
    rolesData,
  };
};

// 🔥 Updated helper functions that use API data
export const usePermissionHelpers = () => {
  const { permissions, isLoading, getPermissionForFeature } = useUserPermissions();

  return {
    getCurrentRole,
    getCurrentRoles,
    hasPermission: getPermissionForFeature,
    isSuperAdmin,
    isAdmin,
    isPlanner,
    isCallCenterAgent,
    permissions,
    isLoading
  };
};

// 🔥 Remove the old functions and replace with API-aware versions
export const getNavigationPermission = (navigationId: string): NavigationPermission => {
  // This is now a fallback - use getNavigationPermissionWithAPI instead
  return getNavigationPermissionWithAPI(navigationId);
};

export const getNavigationWithPermissions = (navigation: any[]): any[] => {
  // This is now a fallback - use useNavigationWithPermissions hook instead
  console.warn('getNavigationWithPermissions is deprecated. Use useNavigationWithPermissions hook instead.');
  return navigation;
};

